/*
 * MarketplaceMatchesCard — v0.5 directive (compact redesign).
 *
 * Renders inline marketplace matches when the problem is rich enough
 * that `marketplace_matcher` returned candidates at ≥ 0.55 fit.
 * Custom Build is ALWAYS shown alongside at 100% — equal-weight, same
 * surface, distinguished only by a yellow accent stripe and a hammer
 * icon. No black-card shouting.
 *
 * Per card: name + score, one serif-italic "fits because…" sentence
 * synthesised from the matcher reasons, optional pricing / deploy /
 * compliance pills, one CTA pill. No bullet checklists.
 *
 * Brand: AAI light theme — cool off-white surface (#eef3ff), yellow
 * accent for Recommended + Custom Build edge, mono for counters /
 * eyebrows, IBM Plex Serif italic for the "why it fits" line.
 */

import React from 'react';
import { ChevronRight, Hammer, Sparkles } from 'lucide-react';

import type { DirectivePayload, MarketplaceMatch } from '../../lib/dossier-types';


export interface MarketplaceMatchesCardProps {
  payload: DirectivePayload;
  /** User clicked one of the marketplace match cards. Parent navigates
   *  to /marketplace/<slug>. */
  onPickMarketplace: (slug: string, match: MarketplaceMatch) => void;
  /** User clicked Custom Build. Parent transitions chat into PRD-draft
   *  proposal flow (no page jump). */
  onPickCustomBuild: () => void;
  picking?: 'marketplace' | 'custom' | null;
}


function scorePct(score: number): string {
  return `${Math.round(score * 100)}%`;
}


/**
 * Compress matcher reasons into one short serif-italic sentence.
 *
 * The matcher emits 1-4 reasons like:
 *   "matches problem category: support"
 *   'your problem mentions "support tier"'
 *   "matches help preference: guided"
 *   "fits org type: startup"
 *
 * Rendering all four as bullets reads like a database explanation.
 * We pick the strongest two and synthesise a sentence the user feels
 * spoken to by, not lectured at.
 */
function synthesiseReason(reasons: string[]): string {
  if (!reasons || reasons.length === 0) return 'Strong shape match for what you described.';
  // Prefer the user-quoted "your problem mentions ..." reason — it
  // echoes the user's own words back, which lands harder than a
  // taxonomy match.
  const sortedByPunch = [...reasons].sort((a, b) => {
    const ap = a.toLowerCase().includes('your problem') ? 0 : 1;
    const bp = b.toLowerCase().includes('your problem') ? 0 : 1;
    return ap - bp;
  });
  const picks = sortedByPunch.slice(0, 2);
  const joined = picks.length === 2 ? `${picks[0]} · ${picks[1]}` : picks[0];
  // First-letter case from the matcher is already lowercase-y; leave it.
  return joined.endsWith('.') ? joined : `${joined}.`;
}


export const MarketplaceMatchesCard: React.FC<MarketplaceMatchesCardProps> = ({
  payload, onPickMarketplace, onPickCustomBuild, picking,
}) => {
  const matches = payload.matches || [];
  const customBuild = payload.custom_build || {
    label: 'Build custom',
    why: 'Built for what off-the-shelf misses.',
  };

  // Sort matches by score descending (server should already, but defensive)
  const sortedMatches = [...matches].sort((a, b) => b.score - a.score);
  const topScore = sortedMatches[0]?.score ?? 0;

  return (
    <div className="aa-directive aa-directive--marketplace">
      <div className="aa-directive-mp-eyebrow">
        <Sparkles size={11} strokeWidth={2.25} />
        What's already on the shelf — and what we'd build instead
      </div>

      <div className="aa-directive-mp-grid">
        {sortedMatches.map((m) => {
          const recommended = m.score === topScore && m.score >= 0.7;
          const isPicking = picking === 'marketplace';
          const disabled = !!picking && !isPicking;
          return (
            <article
              key={m.slug}
              className={
                'aa-directive-mp-card'
                + (recommended ? ' aa-directive-mp-card--rec' : '')
              }
              aria-label={`${m.name} — ${scorePct(m.score)} fit`}
            >
              {recommended && (
                <span className="aa-directive-mp-card-badge">Recommended</span>
              )}
              <header className="aa-directive-mp-card-head">
                <h4 className="aa-directive-mp-card-name">{m.name}</h4>
                <span className="aa-directive-mp-card-score">
                  <span className="aa-directive-mp-card-score-num">{scorePct(m.score)}</span>
                  <span className="aa-directive-mp-card-score-label">fit</span>
                </span>
              </header>
              <p className="aa-directive-mp-card-tagline">{m.tagline}</p>
              <p className="aa-directive-mp-card-why">
                <em>Fits because </em>
                <em>{synthesiseReason(m.reasons)}</em>
              </p>
              {(m.deploy_time || m.pricing || (m.compliance && m.compliance.length > 0)) && (
                <div className="aa-directive-mp-card-meta">
                  {m.deploy_time && (
                    <span className="aa-directive-mp-card-pill">Deploy {m.deploy_time}</span>
                  )}
                  {m.pricing && (
                    <span className="aa-directive-mp-card-pill">{m.pricing}</span>
                  )}
                  {(m.compliance || []).map((c) => (
                    <span key={c} className="aa-directive-mp-card-pill">{c}</span>
                  ))}
                </div>
              )}
              <button
                type="button"
                className="aa-directive-mp-card-cta"
                onClick={() => onPickMarketplace(m.slug, m)}
                disabled={disabled || isPicking}
              >
                {isPicking ? 'Setting up…' : 'Pick this'}
                {!isPicking && <ChevronRight size={12} strokeWidth={2.5} />}
              </button>
            </article>
          );
        })}

        {/* Custom Build — same surface as marketplace cards, distinguished
            by a yellow left-edge accent stripe and a hammer icon. Equal
            weight in the comparison, not a black-card shout. */}
        <article
          className="aa-directive-mp-card aa-directive-mp-card--custom"
          aria-label="Build custom — 100% fit"
        >
          <header className="aa-directive-mp-card-head">
            <h4 className="aa-directive-mp-card-name">
              <Hammer size={13} strokeWidth={2.25} />
              {customBuild.label}
            </h4>
            <span className="aa-directive-mp-card-score">
              <span className="aa-directive-mp-card-score-num">100%</span>
              <span className="aa-directive-mp-card-score-label">fit</span>
            </span>
          </header>
          <p className="aa-directive-mp-card-tagline">Built for you, end to end.</p>
          <p className="aa-directive-mp-card-why">
            <em>{customBuild.why}</em>
          </p>
          <button
            type="button"
            className="aa-directive-mp-card-cta aa-directive-mp-card-cta--custom"
            onClick={onPickCustomBuild}
            disabled={picking === 'marketplace'}
          >
            {picking === 'custom' ? 'Setting up…' : 'Scope a build'}
            {picking !== 'custom' && <ChevronRight size={12} strokeWidth={2.5} />}
          </button>
        </article>
      </div>
    </div>
  );
};

export default MarketplaceMatchesCard;
